import { useState } from "react";
import { classNames } from "primereact/utils";
import styles from "./EmployeePortfolioDialog.module.scss";
import { AppDialog } from "../../../../../../common/components/AppDialog/AppDialog";
import { RenderWhen } from "../../../../../../common/infrastructure/components/RenderWhen";
import { useEmployeePortfolioViewer } from "../../../hooks/useEmployeePortfolioViewer";

interface IEmployeePortfolioDialogProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  employeeName: string;
  serviceId?: number;
  studioId?: number;
}

// Number of skeleton placeholders shown while the image list is being fetched.
const LIST_SKELETON_COUNT = 3;

/** A single portfolio image that shows a shimmer skeleton until it has loaded. */
const PortfolioImage = ({ src, alt }: { src: string; alt: string }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <div className={classNames(styles.imageWrapper, { [styles.loading]: !isLoaded })}>
      <RenderWhen condition={!isLoaded}>
        <div className={styles.skeleton} />
      </RenderWhen>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={classNames(styles.image, { [styles.imageLoaded]: isLoaded })}
        onLoad={() => setIsLoaded(true)}
        onError={() => setIsLoaded(true)}
      />
    </div>
  );
};

export const EmployeePortfolioDialog = ({
  isOpen,
  onClose,
  employeeId,
  employeeName,
  serviceId,
  studioId,
}: IEmployeePortfolioDialogProps) => {
  const { images, isLoading } = useEmployeePortfolioViewer(
    employeeId,
    serviceId,
    studioId,
    isOpen
  );

  return (
    <AppDialog
      header={`Портфолио на ${employeeName}`}
      visible={isOpen}
      onHide={onClose}
      contentClassName={styles.dialogContent}
      style={{ width: "min(520px, 92vw)" }}
    >
      <RenderWhen condition={isLoading}>
        <div className={styles.gallery}>
          {Array.from({ length: LIST_SKELETON_COUNT }).map((_, index) => (
            <div key={index} className={classNames(styles.imageWrapper, styles.loading)}>
              <div className={styles.skeleton} />
            </div>
          ))}
        </div>
      </RenderWhen>

      <RenderWhen condition={!isLoading && images.length === 0}>
        <div className={styles.state}>
          <div className={styles.emptyIcon}>🖼️</div>
          <p>Няма налични снимки в портфолиото.</p>
        </div>
      </RenderWhen>

      <RenderWhen condition={!isLoading && images.length > 0}>
        <div className={styles.gallery}>
          {images.map((image) => (
            <PortfolioImage
              key={image.id}
              src={image.url}
              alt={`Портфолио на ${employeeName}`}
            />
          ))}
        </div>
      </RenderWhen>
    </AppDialog>
  );
};
